export const DEFAULT_MD = `# Homemade Sourdough Bread — A Complete Guide 🍞

A step-by-step recipe for baking a rustic, crusty sourdough loaf at home. No commercial yeast needed — just flour, water, salt, and a living starter.

## Prep Checklist

- [x] **Feed the starter** — 12 hours before mixing
- [x] **Gather ingredients** — bread flour, whole wheat, salt, water
- [ ] **Mix & bulk ferment** — 4–6 hours at room temp
- [ ] **Shape, proof, and bake** — overnight cold proof, bake in the morning

## Ingredients

| Ingredient | Weight | Baker's % |
|---|---|---:|
| Bread flour | 400 g | 80% |
| Whole wheat flour | 100 g | 20% |
| Water (warm) | 375 g | 75% |
| Sourdough starter | 100 g | 20% |
| Fine sea salt | 10 g | 2% |

## The Science Behind It

Sourdough fermentation relies on wild yeast and lactic acid bacteria. The **hydration level** is the ratio of water to flour by weight: $H = \\frac{m_{\\text{water}}}{m_{\\text{flour}}} \\times 100\\%$.

During bulk fermentation, yeast produces $\\text{CO}_2$ through anaerobic respiration:

$$\\text{C}_6\\text{H}_{12}\\text{O}_6 \\xrightarrow{\\text{yeast}} 2\\text{C}_2\\text{H}_5\\text{OH} + 2\\text{CO}_2$$

The rate of fermentation follows an Arrhenius-style temperature dependence:

$$k(T) = A \\cdot e^{-E_a / RT}$$

A common rule of thumb: for every $5°\\text{C}$ increase in dough temperature, fermentation speed roughly doubles. You can estimate proof time $t$ at a given temperature $T$ relative to a reference:

$$t(T) = t_0 \\cdot 2^{(T_0 - T)/5}$$

### Key Molecules

Ethanol and acetic acid — the two main byproducts of fermentation:

\`\`\`smiles
CCO
\`\`\`

\`\`\`smiles
CC(=O)O
\`\`\`

## Method

### Autolyse & Mix

\`\`\`python
# Baker's formula calculator
def recipe(flour_g: float, hydration: float = 0.75, salt: float = 0.02, starter: float = 0.20):
    """Calculate ingredient weights from total flour weight."""
    return {
        "flour":   f"{flour_g:.0f} g",
        "water":   f"{flour_g * hydration:.0f} g",
        "salt":    f"{flour_g * salt:.0f} g",
        "starter": f"{flour_g * starter:.0f} g",
    }

print(recipe(500))
# {'flour': '500 g', 'water': '375 g', 'salt': '10 g', 'starter': '100 g'}
\`\`\`

### Bulk Fermentation Schedule

\`\`\`javascript
// Stretch-and-fold timer
const folds = [
  { time: "0:00", action: "Mix dough, start bulk ferment" },
  { time: "0:30", action: "Stretch & fold #1" },
  { time: "1:00", action: "Stretch & fold #2" },
  { time: "1:30", action: "Stretch & fold #3" },
  { time: "2:00", action: "Stretch & fold #4 (if needed)" },
  { time: "4:00", action: "Check: dough should be 50% larger" },
];

folds.forEach(({ time, action }) => console.log(\`[\${time}] \${action}\`));
\`\`\`

## Baking Day Timeline

\`\`\`mermaid
sequenceDiagram
    participant B as Baker
    participant D as Dough
    participant O as Dutch Oven

    B->>D: Remove from fridge
    B->>O: Preheat to 250 °C (45 min)
    B->>D: Score with razor blade
    B->>O: Place dough inside, lid on
    O->>O: Bake 20 min (steamed)
    B->>O: Remove lid
    O->>O: Bake 20 min (browning)
    O-->>B: Golden loaf ready
\`\`\`

## Troubleshooting

\`\`\`mermaid
flowchart TD
    A["Loaf is too flat"] --> B{"Starter active?"}
    B -- Yes --> C{"Bulk ferment long enough?"}
    C -- Yes --> D["Shaping was too gentle — build more tension"]
    C -- No --> E["Extend bulk ferment by 1 hour"]
    B -- No --> F["Feed starter twice daily for 3 days"]
    F --> G["Float test: spoonful should float in water"]
    G --> H["Retry bake"]
\`\`\`

## Kitchen Playlist

\`\`\`abc
X:1
T:Hot Cross Buns
M:4/4
L:1/4
K:C
E D C2 | E D C2 | C C D D | E D C2 |
\`\`\`

## Tips from the Kitchen

> "The best bread comes from dough that's slightly harder to handle than you'd like. Wetter dough means bigger holes and a better crust."
> — Chad Robertson, *Tartine Bread*

> [!NOTE]
> Use the **poke test** to check proof: press the dough gently — it should spring back slowly, leaving a slight indent.

> [!TIP]
> For an extra-crispy crust, add a few ice cubes to the bottom of the oven when you start baking.

> [!WARNING]
> Do not skip the cold proof. Shaping a warm, fully-fermented dough leads to a flat loaf. Overnight fridge rest firms the dough and develops flavor.

> [!CAUTION]
> The Dutch oven reaches 250 °C — always use heavy oven mitts. Steam burns from removing the lid are the most common baking injury.

## The Finished Loaf

![Freshly baked sourdough bread](https://images.unsplash.com/photo-1509440159596-0249088772ff?w=800&h=400&fit=crop)

## Quick Reference

This recipe makes a **single round loaf** (about 850 g baked). The crumb should be **open** and *slightly tangy*, with ~~dense patches~~ nowhere in sight. Check your starter with the \`float test\` before mixing, and adjust timing with a [bulk ferment calculator](https://www.theperfectloaf.com).

---

*Last baked: Sunday morning — the best alarm clock is the smell of bread in the oven.*
`;
